import { create } from 'zustand';
import { trainingService } from '@/services/training.service';
import type { TrainingGround, TrainingSession, TrainingReview } from '@/types';

interface TrainingState {
  // Grounds
  grounds: TrainingGround[];
  groundsLoading: boolean;
  fetchGrounds: () => Promise<void>;
  createGround: (data: { name: string; description?: string; agent_id: string }) => Promise<TrainingGround>;
  updateGround: (id: string, data: Partial<TrainingGround>) => Promise<void>;
  deleteGround: (id: string) => Promise<void>;

  // Active session
  activeSession: TrainingSession | null;
  sessionMessages: Array<{ sender: 'human' | 'bot'; content: string }>;
  sessionLoading: boolean;
  startSession: (scenarioId: string) => Promise<void>;
  sendMessage: (message: string) => Promise<void>;
  completeSession: () => Promise<TrainingReview>;
  clearSession: () => void;

  // Reviews
  reviews: TrainingReview[];
  reviewsLoading: boolean;
  fetchReviews: (status?: string) => Promise<void>;
  submitVerdict: (reviewId: string, data: { status: string; score: number; feedback: string }) => Promise<void>;
}

export const useTrainingStore = create<TrainingState>((set, get) => ({
  // Grounds
  grounds: [],
  groundsLoading: false,

  fetchGrounds: async () => {
    set({ groundsLoading: true });
    try {
      const grounds = await trainingService.getGrounds();
      set({ grounds, groundsLoading: false });
    } catch {
      set({ groundsLoading: false });
      throw new Error('Failed to fetch training grounds');
    }
  },

  createGround: async (data) => {
    const ground = await trainingService.createGround(data);
    set((state) => ({ grounds: [ground, ...state.grounds] }));
    return ground;
  },

  updateGround: async (id, data) => {
    const updated = await trainingService.updateGround(id, data);
    set((state) => ({
      grounds: state.grounds.map((g) => (g.id === id ? updated : g)),
    }));
  },

  deleteGround: async (id) => {
    await trainingService.deleteGround(id);
    set((state) => ({
      grounds: state.grounds.filter((g) => g.id !== id),
    }));
  },

  // Active session
  activeSession: null,
  sessionMessages: [],
  sessionLoading: false,

  startSession: async (scenarioId) => {
    set({ sessionLoading: true });
    try {
      const response = await trainingService.startSession(scenarioId);
      set({
        activeSession: response.session,
        sessionMessages: [{ sender: 'bot', content: response.first_message }],
        sessionLoading: false,
      });
    } catch {
      set({ sessionLoading: false });
      throw new Error('Failed to start training session');
    }
  },

  sendMessage: async (message) => {
    const { activeSession } = get();
    if (!activeSession) throw new Error('No active session');

    set((state) => ({
      sessionMessages: [...state.sessionMessages, { sender: 'human', content: message }],
      sessionLoading: true,
    }));

    try {
      const response = await trainingService.sendMessage(activeSession.id, message);
      set((state) => ({
        sessionMessages: [...state.sessionMessages, { sender: 'bot', content: response.bot_reply }],
        sessionLoading: false,
      }));
    } catch {
      set({ sessionLoading: false });
      throw new Error('Failed to send message');
    }
  },

  completeSession: async () => {
    const { activeSession } = get();
    if (!activeSession) throw new Error('No active session');

    set({ sessionLoading: true });
    try {
      const response = await trainingService.completeSession(activeSession.id);
      set({ activeSession: null, sessionMessages: [], sessionLoading: false });
      return response.review;
    } catch {
      set({ sessionLoading: false });
      throw new Error('Failed to complete session');
    }
  },

  clearSession: () => set({ activeSession: null, sessionMessages: [] }),

  // Reviews
  reviews: [],
  reviewsLoading: false,

  fetchReviews: async (status?) => {
    set({ reviewsLoading: true });
    try {
      const reviews = await trainingService.getReviews(status);
      set({ reviews, reviewsLoading: false });
    } catch {
      set({ reviewsLoading: false });
      throw new Error('Failed to fetch reviews');
    }
  },

  submitVerdict: async (reviewId, data) => {
    const updated = await trainingService.submitVerdict(reviewId, data);
    set((state) => ({
      reviews: state.reviews.map((r) => (r.id === reviewId ? updated : r)),
    }));
  },
}));
